import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, SquaresFour, NotePencil, ClockCounterClockwise,
  Timer, Airplane, Users, Gear, SignOut, List,
} from "@phosphor-icons/react";
import { useState } from "react";
import ChatWidget from "@/components/ChatWidget";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/submit", label: "Today's Report", icon: NotePencil },
  { to: "/reports", label: "Report History", icon: ClockCounterClockwise },
  { to: "/attendance", label: "Attendance", icon: Timer },
  { to: "/leave", label: "Leave", icon: Airplane },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [...nav];
  if (user?.role === "admin") links.splice(5, 0, { to: "/admin", label: "All Employees", icon: Users });

  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = () => (
    <div className="flex h-full flex-col sidebar-gradient text-zinc-300">
      <div className="flex items-center gap-2.5 px-6 h-16 border-b border-white/10">
        <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
          <GraduationCap size={20} weight="regular" className="text-white" />
        </div>
        <span className="font-heading font-medium text-white text-sm leading-tight">HCC Employee Management System</span>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {links.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                isActive ? "bg-white/10 text-white font-medium" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}>
            <Icon size={19} weight={"regular"} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2">
          <p className="text-sm font-medium text-white truncate">{user?.name}</p>
          <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          {user?.role === "admin" && (
            <span className="inline-block mt-1.5 text-[10px] font-medium uppercase tracking-wider bg-white/15 text-white px-2 py-0.5 rounded-full">Admin</span>
          )}
        </div>
        <Button data-testid="logout-btn" onClick={doLogout} variant="ghost"
          className="w-full justify-start text-zinc-400 hover:text-white hover:bg-white/5">
          <SignOut size={18} className="mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:block w-[250px] shrink-0 fixed inset-y-0 left-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[250px]"><SidebarInner /></div>
        </div>
      )}

      <div className="flex-1 lg:ml-[250px] min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 bg-white/70 backdrop-blur-xl border-b border-border">
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)}><List size={24} /></button>
          <span className="font-heading font-medium">HCC Employee Management System</span>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto">
          <Outlet />
        </main>
      </div>
      <ChatWidget />
    </div>
  );
}
